<template>
  <div id="add-profession">
    <v-card class="document-upload-card" elevation="0">
      <!-- Premium Header -->
      <div class="premium-header">
        <div class="header-content">
          <div class="header-icon-wrapper">
            <v-icon size="32" color="white">mdi-file-document-plus-outline</v-icon>
          </div>
          <div class="header-text">
            <h2 class="header-title">{{ pageTitle }}</h2>
            <p class="header-subtitle">Upload and manage your documents securely</p>
          </div>
        </div>
        <v-btn
          icon
          absolute
          top
          right
          dark
          @click="closePrompt"
          class="close-btn"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Main Content -->
      <v-card-text class="px-8 py-6">
        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="submitForm"
        >
          <!-- Required Field Notice -->
          <v-alert
            dense
            text
            type="info"
            border="left"
            class="mb-6 required-notice"
          >
            <div class="d-flex align-center">
              <v-icon small class="mr-2">mdi-asterisk</v-icon>
              <span class="text-caption font-weight-medium">Fields marked with asterisk are required</span>
            </div>
          </v-alert>

          <v-container fluid class="px-0">
            <v-row>
              <!-- Document Details Section -->
              <v-col cols="12">
                <div class="section-title mb-4">
                  <v-icon color="primary" class="mr-2">mdi-text-box</v-icon>
                  <span class="font-weight-bold">Document Information</span>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label mb-2">
                  <span class="error--text">*</span> Document Type
                </label>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :loading="DocumentTypeOptionsLoading"
                  :items="DocumentTypeOptions"
                  :rules="DocumentTypeRules"
                  v-model="DocumentType"
                  required
                  outlined
                  dense
                  placeholder="Select document type"
                  prepend-inner-icon="mdi-file-document-outline"
                  class="premium-input"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="primary">mdi-file-document-outline</v-icon>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label mb-2">
                  <span class="error--text">*</span> Document Number
                </label>
                <v-text-field
                  v-model="ReferenceNo"
                  :rules="ReferenceNoRules"
                  placeholder="Enter document number"
                  hint="e.g., Passport number, ID number"
                  persistent-hint
                  required
                  outlined
                  dense
                  class="premium-input"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="primary">mdi-pound</v-icon>
                  </template>
                </v-text-field>
              </v-col>

              <!-- Upload Section -->
              <v-col cols="12">
                <v-divider class="my-4"></v-divider>
                <div class="section-title mb-4">
                  <v-icon color="primary" class="mr-2">mdi-cloud-upload</v-icon>
                  <span class="font-weight-bold">Upload Document</span>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="upload-section">
                  <div class="upload-header mb-3">
                    <v-chip small color="primary" text-color="white" class="mb-2">
                      <v-icon left small>mdi-information</v-icon>
                      File Requirements
                    </v-chip>
                    <p class="text-caption text--secondary mb-0">
                      Only JPG and PNG image files are accepted
                    </p>
                  </div>

                  <v-file-input
                    :clearable="false"
                    truncate-length="30"
                    accept="image/png, image/jpeg"
                    placeholder="Click to select file"
                    prepend-icon=""
                    v-model="UploadedImage1"
                    @change="addImage(1)"
                    outlined
                    dense
                    class="premium-file-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary">mdi-paperclip</v-icon>
                    </template>
                    <template v-slot:append-outer>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            color="error"
                            @click="deleteImage(1)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Remove file</span>
                      </v-tooltip>
                    </template>
                  </v-file-input>

                  <!-- Image Edit Tools -->
                  <v-card v-if="SelectedImage1.ImageUrl" outlined class="mt-4 edit-tools-card">
                    <v-card-title class="text-subtitle-2 py-3 px-4 primary lighten-5">
                      <v-icon small color="primary" class="mr-2">mdi-image-edit-outline</v-icon>
                      <span class="font-weight-bold">Image Editing Tools</span>
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <div class="d-flex justify-center flex-wrap">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              small
                              outlined
                              color="primary"
                              class="ma-1"
                              @click="flip(true, false)"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>mdi-flip-horizontal</v-icon>
                            </v-btn>
                          </template>
                          <span>Flip Horizontal</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              small
                              outlined
                              color="primary"
                              class="ma-1"
                              @click="flip(false, true)"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>mdi-flip-vertical</v-icon>
                            </v-btn>
                          </template>
                          <span>Flip Vertical</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              small
                              outlined
                              color="primary"
                              class="ma-1"
                              @click="rotate(90)"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>mdi-rotate-right</v-icon>
                            </v-btn>
                          </template>
                          <span>Rotate Right</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              small
                              outlined
                              color="primary"
                              class="ma-1"
                              @click="rotate(-90)"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>mdi-rotate-left</v-icon>
                            </v-btn>
                          </template>
                          <span>Rotate Left</span>
                        </v-tooltip>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="preview-section">
                  <div class="preview-header mb-3">
                    <v-chip small color="secondary" text-color="white">
                      <v-icon left small>mdi-eye</v-icon>
                      Preview
                    </v-chip>
                  </div>

                  <v-card outlined class="preview-card">
                    <div v-if="!SelectedImage1.ImageUrl" class="preview-placeholder">
                      <v-icon size="64" color="grey lighten-1">mdi-file-image-outline</v-icon>
                      <p class="text-caption text--secondary mt-3 mb-0">
                        Your uploaded image will appear here
                      </p>
                    </div>
                    <cropper
                      v-else
                      ref="cropper"
                      :src="SelectedImage1.ImageUrl"
                      class="cropper-wrapper"
                    />
                  </v-card>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <!-- Premium Footer -->
      <v-divider></v-divider>
      <v-card-actions class="px-6 py-4 action-footer">
        <v-spacer></v-spacer>
        <v-btn
          text
          large
          @click="closePrompt"
          class="px-6"
        >
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
          :disabled="!valid1"
          @click.prevent="submitForm"
          :loading="SubmitFlag"
          color="primary"
          large
          elevation="2"
          class="px-8 ml-3"
        >
          <v-icon left>mdi-check-circle</v-icon>
          Save Document
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import { CircleStencil, Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";

export default {
  mixins: [common],
  components: {
    Cropper,
    CircleStencil,
  },
  props: {
    addRecordPrompt: {
      type: Boolean,
      required: true,
    },
    pageDescription: {
      type: String,
    },
    aspacFlag: {
      type: String,
      default: false,
    },
  },
  data() {
    return {
      pageTitle: "Add document",

      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      UploadFlag: false,

      rows: [],
      member: {},

      DocumentTypeRules: [(v) => !!v || "Document Type is required"],
      DocumentType: "",
      DocumentTypeOptions: [],
      DocumentTypeOptionsLoading: false,

      ReferenceNo: "",
      ReferenceNoRules: [(v) => !!v || "Reference No is required"],

      UploadedImage1: {},
      SelectedImage1: { src: null },
      image: {
        // src: "media/images/jci-logo.png",
        src: "",
        type: null,
      },
    };
  },
  computed: {},
  watch: {
    DocumentTypeOptions: function () {
      console.log("watch DocumentTypeOptions");
      this.LoadingFlag = false;
      this.DocumentTypeOptionsLoading = false;
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    validateForm() {
      console.log("validateForm called");
      this.$refs.form1.validate();
    },
    resetForm() {
      console.log("resetForm called");
      this.$refs.form1.reset();
      this.closePrompt();
    },
    resetValidation() {
      console.log("resetValidation called");
      this.$refs.form1.resetValidation();
    },
    closePrompt() {
      console.log("closePrompt called");
      // this.$emit('Member', this.NewMember);
      this.$emit("hideAddRecordPrompt", this.ResultFlag);
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.getDocumentTypeOptions();
    },
    getDocumentTypeOptions() {
      console.log("getDocumentTypeOptions called");
      this.DocumentTypeOptionsLoading = true;
      var aspacFlag = this.aspacFlag;
      console.log("aspacFlag=" + aspacFlag);
      var userInterface = aspacFlag ? 6 : 3;
      var selectbox1_source = "DocumentType";
      var selectbox1_destination = "DocumentTypeOptions";
      var selectbox1_url = "api/sub-category/options";
      var selectbox1_conditions_array = {
        UserInterface: userInterface,
        CategoryMainName: 'member_document_type',
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    addImage(id) {
      console.log("addImage called");
      console.log({ id });
      switch (id) {
        case 1:
          var file = this.UploadedImage1;
          console.log({ file });
          var src = URL.createObjectURL(file);
          console.log({ src });
          if (this.SelectedImage1.src) {
            URL.revokeObjectURL(this.SelectedImage1.src);
          }
          var FileType = file.type;
          console.log({ FileType });
          this.SelectedImage1 = {
            ImageName: file.name,
            ImageType: FileType,
            ImageUrl: src,
            UploadedImage: file,
          };
          break;

        default:
          break;
      }
    },
    deleteImage(id) {
      console.log("deleteImage called");
      console.log({ id });
      switch (id) {
        case 1:
          this.SelectedImage1 = {
            ImageName: null,
            ImageType: null,
            ImageUrl: null,
            UploadedImage: null,
          };
          this.UploadedImage1 = {};
          break;

        default:
          break;
      }
    },
    flip(x, y) {
      console.log("flip x=" + x + ", y=" + y);
      if (this.$refs.cropper.customImageTransforms.rotate % 180 !== 0) {
        this.$refs.cropper.flip(!x, !y);
      } else {
        this.$refs.cropper.flip(x, y);
      }
    },
    rotate(angle) {
      console.log("rotate=" + angle);
      this.$refs.cropper.rotate(angle);
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        // this.toast("error", "Kindly fill the required fields", true);
      }
    },
    submitForm() {
      console.log("submitForm called");
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      var message = "";
      if (validate1) {
        this.UploadFlag = true;
        const formData = new FormData();
        formData.append("UserInterface", 1);
        formData.append("DocumentType", this.DocumentType);
        formData.append("ReferenceNo", this.ReferenceNo);
        const { canvas } = this.$refs.cropper.getResult();
        if (canvas) {
          var image = "";
          canvas.toBlob((blob) => {
            image = canvas.toDataURL();
            console.log("image=" + image);
            this.selectedImage = image;
            formData.append("UploadedFile1", blob, "logo.png");
            this.uploadImage(formData);
            // Perhaps you should add the setting appropriate file format here
            // this.closePrompt();
          }, "image/jpeg");
        } else {
          //   this.uploadImage(formData);
          message += "Kindly upload image. ";
          //   this.toast("error", message, true);
        }
      } else {
        if (validate1 == false) {
          message += "Kindly fill the required fields. ";
        }
        this.toast("error", message, true);
        // this.sweetAlert("error", message, false);
      }
    },
    uploadImage(upload) {
      console.log("uploadImage is called");

      var UploadFlag = this.UploadFlag;
      console.log("UploadFlag=" + UploadFlag);

      if (UploadFlag) {
        this.SubmitFlag = true;
        this.ResultFlag = false;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members-document-details/store";
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;
            thisIns.UploadFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.resetForm();
              thisIns.ResultFlag = true;
              thisIns.closePrompt();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.UploadFlag = false;
          });
      } else {
        var message = "";
        if (ProfilePicture == "") {
          message += "File is not selected";
        }
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
    this.member = {};
  },
};
</script>
<style lang="scss">
#add-profession {
  .document-upload-card {
    border-radius: 16px !important;
    overflow: hidden;
  }

  /* Premium Header */
  .premium-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 32px;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
      opacity: 0.3;
    }

    .close-btn {
      z-index: 2;
      transition: all 0.3s ease;

      &:hover {
        transform: rotate(90deg);
        background: rgba(255, 255, 255, 0.2);
      }
    }

    .header-content {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      gap: 20px;
    }

    .header-icon-wrapper {
      background: rgba(255, 255, 255, 0.2);
      padding: 16px;
      border-radius: 16px;
      backdrop-filter: blur(10px);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .header-text {
      flex: 1;
    }

    .header-title {
      color: white;
      font-size: 28px;
      font-weight: 700;
      margin: 0;
      letter-spacing: 0.5px;
    }

    .header-subtitle {
      color: rgba(255, 255, 255, 0.95);
      font-size: 14px;
      margin: 4px 0 0 0;
      font-weight: 300;
    }
  }

  /* Required Notice */
  .required-notice {
    border-radius: 12px;
    background: rgba(33, 150, 243, 0.05) !important;
  }

  /* Section Title */
  .section-title {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: #2d3748;
  }

  /* Field Label */
  .field-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    margin-bottom: 8px;
  }

  /* Premium Inputs */
  .premium-input {
    .v-input__slot {
      border-radius: 10px !important;
    }

    .v-text-field__details {
      padding-left: 12px;
    }
  }

  .premium-file-input {
    .v-input__slot {
      border-radius: 10px !important;
    }
  }

  /* Upload Section */
  .upload-section {
    .upload-header {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
      padding: 16px;
      border-radius: 12px;
      border: 1px solid rgba(102, 126, 234, 0.1);
    }
  }

  /* Edit Tools Card */
  .edit-tools-card {
    border-radius: 12px !important;
    border: 1px solid rgba(102, 126, 234, 0.2) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .v-card__title {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
      border-bottom: 1px solid rgba(102, 126, 234, 0.1);
    }

    .v-btn--outlined {
      border-color: rgba(102, 126, 234, 0.5);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(102, 126, 234, 0.1);
        border-color: rgba(102, 126, 234, 1);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  /* Preview Section */
  .preview-section {
    .preview-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .preview-card {
      border-radius: 12px !important;
      min-height: 350px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8f9fa;
      border: 2px dashed #dee2e6 !important;
      overflow: hidden;
    }

    .preview-placeholder {
      text-align: center;
      padding: 40px;
    }

    .cropper-wrapper {
      width: 100%;
      height: 350px;
    }
  }

  /* Action Footer */
  .action-footer {
    background: #f8f9fa;
  }

  /* Cropper Styles */
  .vue-advanced-cropper {
    border-radius: 8px;
    overflow: hidden;
  }

  /* Responsive Design */
  @media (max-width: 960px) {
    .premium-header {
      padding: 24px;

      .header-content {
        flex-direction: column;
        text-align: center;
      }

      .header-title {
        font-size: 24px;
      }

      .header-subtitle {
        font-size: 13px;
      }
    }

    .preview-section {
      .preview-card {
        min-height: 280px;
      }

      .cropper-wrapper {
        height: 280px;
      }
    }
  }

  @media (max-width: 600px) {
    .premium-header {
      padding: 20px;

      .header-icon-wrapper {
        padding: 12px;
      }

      .header-title {
        font-size: 20px;
      }
    }

    .section-title {
      font-size: 14px;
    }

    .preview-section {
      .preview-card {
        min-height: 220px;
      }

      .cropper-wrapper {
        height: 220px;
      }
    }
  }
}
</style>